<template>
  <div class="editor-wrapper">
    <b-loading :is-full-page="isFullPage" :active.sync="isLoading"></b-loading>

    <b-field label="Название документа" label-position="on-border">
      <b-input v-model="title"></b-input>
    </b-field>

    <slot></slot>

    <div id="print" v-show="false" v-if="printAllowed">
      <div class="content" v-html="editorData"></div>
    </div>

    <div class="content">
      <ckeditor :editor="editor" :config="editorConfig" v-model="editorData" @ready="onReady" ref="editor"></ckeditor>
    </div>
  </div>
</template>

<script>
import CKEditor from '@ckeditor/ckeditor5-vue';
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import Link from '@ckeditor/ckeditor5-link/src/link';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import List from '@ckeditor/ckeditor5-list/src/list';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import Indent from '@ckeditor/ckeditor5-indent/src/indent';
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock';
import Font from '@ckeditor/ckeditor5-font/src/font';
import RemoveFormat from '@ckeditor/ckeditor5-remove-format/src/removeformat';
import Table from '@ckeditor/ckeditor5-table/src/table';
import Mention from '@ckeditor/ckeditor5-mention/src/mention';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import EasyImage from '@ckeditor/ckeditor5-easy-image/src/easyimage';
import Image from '@ckeditor/ckeditor5-image/src/image';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageCaption from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize';
import PageBreak from '@ckeditor/ckeditor5-page-break/src/pagebreak';
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import { CustomTableClass } from '@/plugins/CustomTableClass';

import Base64UploadAdapter from '@ckeditor/ckeditor5-upload/src/adapters/base64uploadadapter';
import CKEditorInspector from '@ckeditor/ckeditor5-inspector';

export default {
  name: 'TextEditor',
  components: {
    ckeditor: CKEditor.component
  },
  props: {
    source: {
      type: Boolean
    },
    document: {
      type: Object
    },
    printAllowed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isFullPage: true,
      isLoading: true,
      title: this.document.name || 'Новый документ от ' + new Date().toLocaleString('ru-RU'),
      // CKE
      editorInstance: null,
      editor: ClassicEditor,
      editorData: this.document.data.content,
      editorConfig: {
        placeholder: 'Пустой документ',
        plugins: [
          Essentials,
          Bold,
          Italic,
          Underline,
          Link,
          Heading,
          List,
          Font,
          Paragraph,
          Alignment,
          Indent,
          IndentBlock,
          RemoveFormat,
          Table,
          TableToolbar,
          CustomTableClass,
          Mention,
          Image,
          Base64UploadAdapter,
          EasyImage,
          ImageToolbar,
          ImageCaption,
          ImageStyle,
          ImageResize,
          PageBreak,
          HorizontalLine
        ],

        toolbar: {
          items: [
            'undo',
            'redo',
            '|',
            'heading',
            'fontSize',
            'bold',
            'italic',
            'underline',
            '|',
            'alignment',
            'outdent',
            'indent',
            '|',
            'bulletedList',
            'numberedList',
            'insertTable',
            'imageUpload',
            'link',
            'horizontalLine',
            '|',
            'pageBreak',
            'removeFormat'
          ]
        },

        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
        },

        fontSize: {
          options: ['tiny', 'default', 'big', 'huge']
        },

        image: {
          // You need to configure the image toolbar, too, so it uses the new style buttons.
          toolbar: [
            'imageTextAlternative',
            '|',
            'imageStyle:alignLeft',
            'imageStyle:alignCenter',
            'imageStyle:alignRight',
            'imageStyle:full',
            'imageStyle:side'
          ],

          styles: ['full', 'side', 'alignLeft', 'alignCenter', 'alignRight']
        },

        indentBlock: {
          offset: 1,
          unit: 'em'
        },

        mention: {
          feeds: [
            {
              marker: '_',
              feed: this.getMentionFeedItems,
              itemRenderer: this.customMentionItemRenderer,
              minimumCharacters: 0
            }
          ]
        }
      }
    };
  },
  mounted() {
    this.$root.$on('pushShortCode', ShortCode => {
      this.addMention(ShortCode);
    });

    this.$root.$on('removeShortCode', ShortCode => {
      this.removeMention(ShortCode);
    });

    this.$root.$on('editShortCode', ShortCode => {
      this.editMention(ShortCode);
    });
  },
  computed: {
    modifiedVariables() {
      const { variables } = this.document.data;

      if (!variables) {
        return;
      }

      return variables.map(el => {
        const { name, ...rest } = el;
        return {
          id: '_' + el.name,
          ...rest
        };
      });
    },

    modifiedCodes() {
      const { codes } = this.document.data;

      if (!codes) {
        return;
      }

      return codes.map(el => {
        const { name, ...rest } = el;
        return {
          id: '_' + el.name,
          ...rest
        };
      });
    }
  },

  methods: {
    addMention(mention) {
      this.editorInstance.execute('mention', {
        marker: '_',
        mention: mention
      });
    },

    removeMention(mention) {
      let newEditorData = this.editorData;

      const findAndReplace = (search, replacement) => {
        return newEditorData.split(search).join(replacement);
      };

      const { codes } = this.document.data;

      this.editorData = findAndReplace(`_${mention}`, '');
    },

    editMention(mention) {
      // проверяем новое имя на совпадение со старым
      if (mention.oldName === mention.newName) {
        return;
      }

      let newEditorData = this.editorData;

      // ищем и заменяем все вхождения кода
      const findAndReplace = (search, replacement) => {
        return newEditorData.split(search).join(replacement);
      };

      const { codes } = this.document.data;

      this.editorData = findAndReplace(`_${mention.oldName}`, `_${mention.newName}`);
    },

    onReady(editor) {
      // pass instance instance var
      this.isLoading = false;
      this.editorInstance = editor;
      this.$emit('loaded');

      // run inspector in DEV
      if (process.env.NODE_ENV === 'development') {
        CKEditorInspector.attach(editor);
      }
    },

    getMentionFeedItems(queryText) {
      // As an example of an asynchronous action, return a promise
      // that resolves after a 100ms timeout.
      // This can be a server request or any sort of delayed action.
      return new Promise(resolve => {
        setTimeout(() => {
          const mentionsToDisplay = [...(this.modifiedVariables || []), ...(this.modifiedCodes || [])]
            // Filter out the full list of all items to only those matching the query text.
            .filter(isMentionItemMatching)
            // Return 10 items max - needed for generic queries when the list may contain hundreds of elements.
            .slice(0, 15);

          resolve(mentionsToDisplay);
        }, 100);
      });

      function isMentionItemMatching(item) {
        // Make the search case-insensitive.
        const searchString = queryText.toLowerCase();

        return item.id.toLowerCase().includes(searchString);
      }
    },

    customMentionItemRenderer(item) {
      const itemElement = document.createElement('div');
      itemElement.textContent = `${item.id} `;

      return itemElement;
    }
  }
};
</script>

<style>
:root {
  --ck-color-mention-background: hsla(48, 100%, 65%, 0.5);
  --ck-color-mention-text: hsl(56, 86%, 11%);
}
</style>
